import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Plus, Trash2, X, Save, Edit3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { id: 'em_andamento', label: 'Em Andamento', color: 'bg-blue-500/10 text-blue-400' },
  { id: 'em_revisao', label: 'Em Revisão', color: 'bg-amber-500/10 text-amber-400' },
  { id: 'concluido', label: 'Concluído', color: 'bg-emerald-500/10 text-emerald-400' },
  { id: 'pausado', label: 'Pausado', color: 'bg-gray-500/10 text-gray-400' },
];

const emptyForm = { title: '', client_id: '', project_type: 'sistema', status: 'em_andamento', value: 0, deadline: '', description: '' };

export default function ProjectsManagement() {
  const { getAuthHeader } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/admin/projects`, { headers: getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/admin/clients`, { headers: getAuthHeader(), withCredentials: true }),
      ]);
      setProjects(p.data);
      setClients(c.data);
    } catch (e) { logger.error('Failed to load projects:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (project) => {
    setEditingId(project.id);
    setForm({
      title: project.title || '',
      client_id: project.client_id || '',
      project_type: project.project_type || 'sistema',
      status: project.status || 'em_andamento',
      value: project.value || 0,
      deadline: project.deadline ? project.deadline.slice(0, 10) : '',
      description: project.description || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0 };
      if (editingId) {
        await axios.patch(`${API}/admin/projects/${editingId}`, payload,
          { headers: getAuthHeader(), withCredentials: true });
      } else {
        await axios.post(`${API}/admin/projects`, payload,
          { headers: getAuthHeader(), withCredentials: true });
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente remover este projeto?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, { headers: getAuthHeader(), withCredentials: true });
      load();
    } catch (e) { logger.error('Failed to delete project:', e); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '—';
  const getStatusBadge = (id) => STATUS_OPTIONS.find(s => s.id === id) || STATUS_OPTIONS[0];

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[#22D3EE]" size={40} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="projects-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Gestão de Projetos</h1>
          <p className="text-[#94A3B8]">{projects.length} projetos ativos • Clique em editar para alterar</p>
        </div>
        <button
          onClick={openCreate}
          disabled={clients.length === 0}
          data-testid="add-project-button"
          className="flex items-center gap-2 bg-[#22D3EE] text-[#080710] px-5 py-2.5 rounded-lg font-semibold hover:shadow-[0_0_20px_rgba(34,211,238,0.5)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          title={clients.length === 0 ? 'Cadastre um cliente primeiro' : ''}
        >
          <Plus size={18} /> Novo Projeto
        </button>
      </div>

      {clients.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-400 text-sm">
          Cadastre ao menos um cliente antes de criar projetos.
        </div>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
            data-testid="project-form-modal"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0d0f17] border border-[#22D3EE]/30 rounded-2xl p-6 w-full max-w-md my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-[#F8FAFC]">{editingId ? 'Editar Projeto' : 'Novo Projeto'}</h2>
                <button onClick={() => setShowForm(false)} className="text-[#94A3B8] hover:text-[#F8FAFC]"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  data-testid="project-title-input"
                  type="text" required placeholder="Título do projeto*"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <select
                  required
                  data-testid="project-client-select"
                  value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                >
                  <option value="">Selecione um cliente*</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select
                  data-testid="project-type-select"
                  value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                >
                  <option value="landing">Landing Page</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="sistema">Sistema Web</option>
                  <option value="site">Site Institucional</option>
                  <option value="automacao">Automação</option>
                </select>
                <select
                  value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                >
                  {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <input
                  type="number" step="0.01" placeholder="Valor (R$)"
                  value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <input
                  type="date" placeholder="Prazo"
                  value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <textarea
                  placeholder="Descrição"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none resize-none"
                />
                <button
                  type="submit" disabled={saving}
                  data-testid="save-project-button"
                  className="w-full bg-[#22D3EE] text-[#080710] py-2.5 rounded-lg font-semibold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                  {saving ? 'Salvando...' : (editingId ? 'Salvar Alterações' : 'Criar Projeto')}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {projects.length === 0 ? (
        <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-12 text-center">
          <p className="text-[#94A3B8]">Nenhum projeto cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const status = getStatusBadge(p.status);
            return (
              <div key={p.id} data-testid={`project-card-${p.id}`} className="bg-[#0d0f17] border border-white/5 hover:border-[#22D3EE]/30 rounded-xl p-5 transition-all">
                <h3 className="font-semibold text-[#F8FAFC] mb-2">{p.title}</h3>
                <p className="text-sm text-[#94A3B8] mb-3">Cliente: {getClientName(p.client_id)}</p>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-xs bg-[#22D3EE]/10 text-[#22D3EE] px-2 py-1 rounded">{p.project_type}</span>
                  <span className={`text-xs px-2 py-1 rounded ${status.color}`}>{status.label}</span>
                </div>
                {p.description && (
                  <p className="text-xs text-[#94A3B8] mb-3 line-clamp-2">{p.description}</p>
                )}
                <div className="pt-3 border-t border-white/5 flex items-center justify-between mb-3">
                  <p className="text-lg font-semibold text-[#22D3EE]">
                    R$ {(p.value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  {p.deadline && <p className="text-xs text-[#94A3B8]">{new Date(p.deadline).toLocaleDateString('pt-BR')}</p>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-project-${p.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded bg-[#22D3EE]/10 hover:bg-[#22D3EE]/20 text-[#22D3EE] text-xs font-semibold transition-colors"
                  >
                    <Edit3 size={14} /> Editar
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    data-testid={`delete-project-${p.id}`}
                    className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                    title="Excluir"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
